import { useUnmount } from "ahooks";
import { useMount } from "ahooks-v2";
import React, { useState, forwardRef, useCallback, useImperativeHandle, useRef, useEffect } from "react";
import Player, { Events, Util, I18N } from "xgplayer";
import HlsPlugin from "xgplayer-hls";
import ZH_HK from "xgplayer/es/lang/zh-hk";
import "xgplayer/dist/index.min.css";
import "./vplayer.module.less";

interface PlayerProps {
  id: any;
  url: string;
  // 是否自动播放
  autoplay?: boolean;
  // 是否自动静音自动播放
  autoplayMuted?: boolean;
  // 设置/返回视频静音状态
  muted?: boolean;
  // 是否循环播放
  loop?: boolean;
  // 封面图地址
  poster?: string;
  // 初始起播时间
  startTime?: number;
  // fixed 保持容器宽/高，不做适配 fixWidth 保持容器宽度，适配高度 fixHeight 保持容器高度，适配宽度 auto 根据视频溢出宽/高，适配容器宽/高
  fitVideoSize?: "fixHeight" | "fixed" | "fixWidth";
  // video画面填充模式
  videoFillMode?: "auto" | "contain" | "fill" | "fillHeight" | "fillWidth";
  // 播放器进度条故事点信息
  progressDot?: any[];
  // 进度条预览图配置
  thumbnail?: any;
  // 是否开启画面和控制栏分离模式
  marginControls?: boolean;
  // 是否默认静音, 只在初始化的时候生效
  defaultMuted?: boolean;
  // 提示
  tips?: any;
  children?: any;
  // 是否通关闭video的click/touchend行为切换播放暂停
  closeVideoClick?: boolean;
  // icons
  icons?: any;
  // 播放结束之后是否允许seek
  allowSeekAfterEnded?: boolean;
  // 一些关键点颜色配置
  commonStyle?: any;
  // 允许切换播放状态
  allowTogglePlayStatus?: boolean;
  // 倍速插件显示列表
  playbackRate?: boolean | any;
  // 为是否通关闭video的click/touchend行为切换播放暂停
  closeVideoDblclick?: boolean;
  // 不使用的插件
  ignores?: any;
  controls?: any;
  renderCustomProgressBar?: () => any;
  // 播放时触发,需要注意，自动播放模式下，执行顺序为play->load->autoplay
  onPlay?: (player: Player) => void;
  // 加载完触发
  onLoad?: (player: Player, options?: any) => void;
  // 自动播放开始时触发
  onAutoPlay?: (player: Player) => void;
  // 错误时触发
  onError?: (player: Player, error: Error) => void;
  // 时间触发器（第几秒执行某个事件）
  onTimeHandler?: (time: number) => void;
  // 控制栏播放下一个视频按钮数据
  playNextUrlList?: string[];
  onPlayNext?: () => void;
  // 播放结束时触发
  onEnd?: (player: Player) => void;
}

I18N.use(ZH_HK);
const VPlayer = forwardRef<{}, PlayerProps>(
  (
    {
      children,
      id,
      url,
      autoplay,
      autoplayMuted = false,
      muted = false,
      loop = true,
      poster,
      startTime,
      fitVideoSize,
      videoFillMode,
      commonStyle,
      marginControls = false,
      closeVideoClick = false,
      allowSeekAfterEnded = false,
      defaultMuted = false,
      icons = {
        pause: () => {
          return Util.createDom(
            "div",
            '<img style="width:28px;height:26px" src="https://photo.8891.com.tw/2024/08/19/1724037684105480.png">',
            {},
            "customclass",
          );
        },
        startPlay: () => {
          return Util.createDom(
            "div",
            '<img style="width:70px;height:70px" src="https://photo.8891.com.tw/2024/01/17/1705474610071010.png">',
            {},
            "customclass",
          );
        },
        play: () => {
          return Util.createDom(
            "div",
            '<img style="width:28px;height:28px" src="https://photo.8891.com.tw/2024/01/17/1705474474116576.png">',
            {},
            "customclass",
          );
        },
        playNext: () => {
          return Util.createDom(
            "div",
            '<img style="width:28px;height:28px" src="https://photo.8891.com.tw/2024/01/17/1705474474214320.png">',
            {},
            "customclass",
          );
        },
      },
      onAutoPlay,
      onError,
      onLoad,
      onPlay,
      onEnd,
      playNextUrlList,
      onTimeHandler,
      onPlayNext,
    },
    ref,
  ) => {
    const [playerInstance, setPlayerInstance] = useState<Player | null>(null);
    const videoRef = useRef(null);

    const options = useCallback(() => {
      return {
        id,
        url,
        lang: "zh-hk",
        height: "100%",
        width: "100%",
        playsinline: true,
        autoplay,
        autoplayMuted,
        muted,
        loop,
        startTime,
        fitVideoSize,
        videoFillMode,
        poster,
        marginControls,
        defaultMuted,
        icons,
        commonStyle: {
          ...commonStyle,
          // 进度条底色
          progressColor: "#E8E8E8",
          // 播放完成部分进度条底色
          playedColor: "#3264FF",
          // 进度条滑块样式
          sliderBtnStyle: {
            background: "rgb(50 100 255 / 20%)",
          },
          // 音量颜色
          volumeColor: "#3264FF",
        },
        closeVideoClick,
        fullscreenTarget: document.querySelector("#player-container") as any,
        allowSeekAfterEnded,
        plugins: [HlsPlugin],
        pip: true,
        playNext: {
          urlList: playNextUrlList && playNextUrlList.length > 0 ? [""] : [],
        },
      };
    }, [
      icons,
      commonStyle,
      allowSeekAfterEnded,
      autoplay,
      autoplayMuted,
      closeVideoClick,
      defaultMuted,
      fitVideoSize,
      id,
      ,
      loop,
      marginControls,
      muted,
      poster,
      startTime,
      url,
      videoFillMode,
    ]);

    const initEvent = useCallback(
      (player: Player) => {
        player.on(Events.PLAY, () => {
          onPlay?.(player);
        });
        player.on(Events.LOAD_START, () => {
          onLoad?.(player);
        });
        player.on(Events.AUTOPLAY_STARTED, () => {
          onAutoPlay?.(player);
        });
        player.on(Events.ERROR, (error) => {
          onError?.(player, error);
        });
        player.on(Events.ENDED, () => {
          // 自动播放loop为true时ENDED不生效
          onEnd?.(player);
        });
        // 点击控制栏播放下一个视频按钮
        player.on(Events.PLAYNEXT, () => {
          onPlayNext?.();
        });
        player.on(Events.TIME_UPDATE, () => {
          const currentTime = player.currentTime; // 获取当前播放时间
          onTimeHandler?.(currentTime);
        });
      },
      [onAutoPlay, onError, onLoad, onPlay, onEnd],
    );
    const initPlayer = useCallback(() => {
      const player = new Player(options());
      initEvent(player);
      setPlayerInstance(player);
    }, [options, initEvent]);

    const updatePlayer = () => {
      playerInstance?.resetState();
      playerInstance?.destroy();
      const player = new Player(options());
      initEvent(player);
      setPlayerInstance(player);
    };
    const exit = () => {
      playerInstance?.destroy();
    };

    useMount(() => {
      initPlayer();
    });

    useUnmount(() => {
      exit();
    });

    useImperativeHandle(ref, () => ({
      getInstance: () => playerInstance,
      init: initPlayer,
      updatePlayer,
      play: () => {
        return playerInstance?.play();
      },
      pause: () => {
        playerInstance?.pause();
      },
    }));

    return (
      <div id='player-container' className='w-full h-full relative' ref={videoRef}>
        <div id={id} className='w-full h-full'></div>
        {children}
      </div>
    );
  },
);
VPlayer.displayName = "Player";

export default VPlayer;
